import { getAccessToken } from '@/auth/getAccessToken';

// Cliente propio del inventario de formaleta.
//
// NO vive en `api/client.ts` a propósito: Daniel pidió el 21-oct-2026 que la
// pantalla nazca SEPARABLE porque se va a independizar de ArquiFlow. Repite las
// ~20 líneas de `request` en vez de importarlas (misma decisión que
// `api/laminas.ts`) para que el día que se mude se vayan tres carpetas y
// `client.ts` nunca se haya tocado.

const BASE = `${import.meta.env.VITE_API_BASE_URL || ''}/api`;

/**
 * «No hubo respuesta»: el `fetch` ni salió o se cortó en el camino.
 *
 * Existe porque la cola offline (F-6) tiene que tomar DOS decisiones opuestas
 * sobre el mismo `Error`. Un 400 del servidor es definitivo —reintentarlo mil
 * veces no lo va a arreglar— mientras que un corte de red es exactamente lo
 * que la cola está hecha para esperar. `pedir` las lanzaba indistinguibles, así
 * que un rechazo del backend habría quedado reintentándose para siempre y
 * tapando a las piezas buenas que venían detrás.
 */
export class SinRed extends Error {
  constructor(causa?: unknown) {
    super('No hay conexión con el servidor.');
    this.name = 'SinRed';
    this.cause = causa;
  }
}

async function pedir<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opts.headers as Record<string, string> || {}),
  };
  const token = await getAccessToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { ...opts, headers });
  } catch (e) {
    // Un `fetch` que LANZA no vio ningún servidor: red caída, DNS, túnel.
    // Un servidor que contesta 500 sí entra por el camino de abajo.
    throw new SinRed(e);
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((body as { error?: string }).error || `HTTP ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

/**
 * Sube los BYTES crudos de una foto con su Content-Type. No va por `pedir`
 * porque ese fuerza `application/json`, y acá el cuerpo es la imagen que
 * entregó la cámara del teléfono.
 */
async function subirFoto<T>(path: string, archivo: Blob): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': archivo.type || 'image/jpeg' };
  const token = await getAccessToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, { method: 'POST', headers, body: archivo });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((body as { error?: string }).error || `HTTP ${res.status}`);
  }
  return res.json();
}

// ── Tipos ───────────────────────────────────────────────────────────────────

export interface CategoriaFormaleta {
  id: number;
  nombre: string | null;
  /** Las 3 letras (`PRE`, `ANP`). Vienen ya recortadas: en SQL es `nchar(10)`. */
  abreviatura: string | null;
  /**
   * ⚠️ Es DATO, no decisión de diseño: sale de `FormCategoria.ColorHEXCat`.
   * Prohibición expresa del expediente: no se hardcodea en el frontend.
   * Y no están validados para sol directo en obra — `#00F5D4` sobre blanco no
   * se lee afuera. Por eso la UI los usa como barra/relleno de pastilla con
   * texto calculado por luminancia, nunca como color de texto.
   */
  colorHex: string | null;
  renglones: number;
  piezasDeclaradas: number;
}

export interface PedidoFormaleta {
  /** `1er/2do/3er Pedido` = comprado · `AD01` = fabricado por Adelante. */
  pedido: string;
  renglones: number;
  piezasDeclaradas: number;
}

export interface EstadoPieza {
  id: number;
  estado: string;
  abreviatura: string | null;
  colorHex: string | null;
  /** Cuenta como usable para «¿hay alguna disponible?». */
  disponible: boolean;
  orden: number;
}

export interface TotalesFormaleta {
  modelos: number;
  renglones: number;
  /** Suma de `Cantidad`: lo que dice la COMPRA que hay. */
  piezasDeclaradas: number;
  /** COUNT de FormPieza: lo identificado UNA POR UNA. */
  piezasInventariadas: number;
  renglonesSinCategoria: number;
  piezasSinCategoria: number;
  renglonesSinFoto: number;
}

export interface CatalogosFormaleta {
  categorias: CategoriaFormaleta[];
  pedidos: PedidoFormaleta[];
  estados: EstadoPieza[];
  totales: TotalesFormaleta;
  /** false = la tabla `dbo.FormPieza` todavía no existe (script 033 sin ejecutar). */
  piezasHabilitadas: boolean;
  estadosHabilitados: boolean;
  /** El texto literal que hay que mostrar EN ROJO cuando algo no está. */
  motivoDeshabilitado: string | null;
}

/** El nivel MODELO. No sale de ninguna tabla: lo calcula el backend agrupando por `Nombre`. */
export interface ModeloFormaleta {
  nombre: string;
  renglones: number;
  piezasDeclaradas: number;
  piezasInventariadas: number;
  idCategoria: number | null;
  categoria: string | null;
  abreviatura: string | null;
  colorHex: string | null;
  ancho: number | null;
  largo: number | null;
  /** `Dimension3` en la base. En campo se le dice «fondo». */
  fondo: number | null;
  unidad: string | null;
  fabricante: string | null;
  /** Lo fabrica nuestra planta. Se decide por `Pedido`/`Fabricante`, nunca parseando el nombre. */
  fabPropia: boolean;
  pedidos: string[];
  anioFab: number | null;
  urlFoto: string | null;
}

export interface PiezaFormaleta {
  id: number;
  /** `AD-00412`. Computada PERSISTED sobre el IDENTITY: única por construcción. */
  consecutivo: string | null;
  idForm: number;
  modelo: string | null;
  categoria: string | null;
  abreviatura: string | null;
  colorHex: string | null;
  idEstado: number | null;
  estado: string | null;
  colorEstado: string | null;
  disponible: boolean;
  ubicacion: string | null;
  idObra: string | null;
  latitud: number | null;
  longitud: number | null;
  fechaGeo: string | null;
  /** El `IDLote` crudo. Para mostrar usá `lote`, que es lo legible. */
  idLote: number | null;
  /** El código que una persona reconoce: `VI-8.20`. Sale de `Lotes.IDBD`. */
  lote: string | null;
  /** 0 = la coordenada cae ADENTRO del polígono; >0 = metros al borde. */
  loteDistanciaM: number | null;
  /** Cuánto más cerca está este lote que el siguiente. Es la CERTEZA. */
  loteMargenM: number | null;
  /**
   * ⚠️ Viaja RESUELTO por el backend y NO se recalcula acá. La regla
   * (`margen > error del GPS`) es la decisión de producto de F-4; replicada en
   * el frontend se desincroniza el día que cambie.
   */
  loteConfiable: boolean;
  /** NULL = el barrido todavía no pasó por esta pieza (≠ «pasó y no había lote»). */
  fechaLote: string | null;
  fechaUltLavado: string | null;
  fechaUltMantenimiento: string | null;
  fechaMarcado: string | null;
  qr: string | null;
  epc: string | null;
  urlFoto: string | null;
  fechaCreacion: string;
}

export interface RenglonFormaleta {
  idForm: number;
  nombre: string | null;
  piezasDeclaradas: number;
  piezasInventariadas: number;
  pedido: string | null;
  idFab: string | null;
  fabricante: string | null;
  anioFab: number | null;
  unidad: string | null;
  fabPropia: boolean;
  urlFoto: string | null;
  fechaCreacion: string | null;
  piezas: PiezaFormaleta[];
}

export interface ModeloDetalle {
  modelo: ModeloFormaleta;
  renglones: RenglonFormaleta[];
  piezasHabilitadas: boolean;
  motivoDeshabilitado: string | null;
}

export interface PaginaPiezas {
  piezas: PiezaFormaleta[];
  total: number;
  mostradas: number;
  piezasHabilitadas: boolean;
  motivo: string | null;
}

export interface ObraFormaleta {
  obra: string;
  subdivision: string | null;
}

/** El modelo que nunca se inventarió. El `nombre` lo autogenera el backend. */
export interface NuevoModelo {
  idCategoria: number | null;
  ancho: number | null;
  largo: number | null;
  fondo: number | null;
  unidad: string | null;
}

export interface AltaPiezas {
  idForm?: number | null;
  nuevoModelo?: NuevoModelo | null;
  /** Cuántas iguales hay ahí. Tope 200: cada una emite un QR que hay que marcar a mano. */
  cuantas: number;
  ubicacion?: string | null;
  idObra?: string | null;
  idEstado?: number | null;
  latitud?: number | null;
  longitud?: number | null;
  /**
   * Metros de error del GPS (`coords.accuracy`). Viaja aunque la pantalla de
   * campo no lo pregunte: es lo que después decide si el barrido retroactivo
   * contra `geo.Geometria` puede confiar en la coordenada para resolver el lote.
   */
  precisionGpsM?: number | null;
  /** «Le pegué la marca ahora mismo» → sella `FechaMarcado`. */
  marcadaAhora: boolean;
}

export interface AltaResultado {
  idForm: number;
  nombre: string | null;
  renglonCreado: boolean;
  /** El nombre autogenerado ya existía: se usó ese renglón en vez de duplicar el modelo. */
  renglonReusado: boolean;
  piezas: PiezaFormaleta[];
}

/** A qué nivel del modelo se le cuelga la foto: el modelo (renglón) o la pieza. */
export type DestinoFoto = 'renglon' | 'pieza';

export interface FotoFirma {
  /** null = el carril directo no está disponible; hay que usar `fotoRenglon`/`fotoPieza`. */
  nombre: string | null;
  url: string | null;
  contentType: string | null;
}

export interface FotoResultado {
  urlFoto: string | null;
  /** La pieza se guardó pero la foto no: el blob no está configurado. */
  advertencia: string | null;
}

/**
 * Lo que devuelve el barrido retroactivo de lotes. `dudosas` NO es un error:
 * son piezas a las que SÍ se les resolvió un lote, pero cuyo margen contra el
 * lote vecino no le gana al error del GPS. Decirlas aparte es el punto: un lote
 * dudoso presentado como certero es el dato caro.
 */
export interface BarridoLotes {
  revisadas: number;
  conLote: number;
  confiables: number;
  dudosas: number;
  /** La coordenada no cayó en ningún polígono ni cerca de uno. */
  sinLote: number;
  fallaron: number;
  /** Se llegó al tope de 500 de la pasada: hay que volver a correrlo. */
  quedanPendientes: boolean;
}

export interface FiltroModelos {
  q?: string;
  idCategoria?: number | null;
  pedido?: string | null;
  sinCategoria?: boolean;
}

export interface FiltroPiezas extends FiltroModelos {
  idEstado?: number | null;
  idForm?: number | null;
  soloDisponibles?: boolean;
  take?: number;
}

// ── Llamadas ────────────────────────────────────────────────────────────────

function qs(f: FiltroPiezas): string {
  const p = new URLSearchParams();
  if (f.q) p.set('q', f.q);
  if (f.sinCategoria) p.set('sinCategoria', '1');
  else if (f.idCategoria) p.set('categoria', String(f.idCategoria));
  if (f.pedido) p.set('pedido', f.pedido);
  if (f.idEstado) p.set('estado', String(f.idEstado));
  if (f.idForm) p.set('renglon', String(f.idForm));
  if (f.soloDisponibles) p.set('disponibles', '1');
  if (f.take) p.set('take', String(f.take));
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const apiFormaleta = {
  catalogos: () => pedir<CatalogosFormaleta>('/formaleta/catalogos'),

  modelos: (f: FiltroModelos = {}) => pedir<ModeloFormaleta[]>(`/formaleta/modelos${qs(f)}`),

  /**
   * ⚠️ El nombre viaja por QUERY STRING, no como segmento de ruta: los nombres
   * reales traen `/`, `&` y espacios (`/PROD-BCSPECIAL ADJUSTABLE WALER BRKT`
   * arranca con slash) y como segmento partirían la ruta en dos.
   */
  modelo: (nombre: string) =>
    pedir<ModeloDetalle>(`/formaleta/modelo?nombre=${encodeURIComponent(nombre)}`),

  piezas: (f: FiltroPiezas = {}) => pedir<PaginaPiezas>(`/formaleta/piezas${qs(f)}`),

  /** Resolver un escaneo o un consecutivo tecleado (`412`, `AD-00412`, `ad-412`). */
  porCodigo: (codigo: string) =>
    pedir<PiezaFormaleta>(`/formaleta/pieza?codigo=${encodeURIComponent(codigo)}`),

  inventariar: (dto: AltaPiezas) =>
    pedir<AltaResultado>('/formaleta/piezas', { method: 'POST', body: JSON.stringify(dto) }),

  fotoRenglon: (idForm: number, archivo: Blob) =>
    subirFoto<FotoResultado>(`/formaleta/renglones/${idForm}/foto`, archivo),

  fotoPieza: (id: number, archivo: Blob) =>
    subirFoto<FotoResultado>(`/formaleta/piezas/${id}/foto`, archivo),

  /** Pide la URL firmada para subir directo al blob (F-3). `nombre: null` = no disponible. */
  fotoFirma: (tipo: DestinoFoto, id: number, contentType: string) =>
    pedir<FotoFirma>('/formaleta/fotos/firma', {
      method: 'POST', body: JSON.stringify({ tipo, id, contentType }),
    }),

  /** Anota en la fila la foto que YA se subió por el carril directo. */
  fotoAsignar: (tipo: DestinoFoto, id: number, nombre: string) =>
    pedir<FotoResultado>('/formaleta/fotos/asignar', {
      method: 'PUT', body: JSON.stringify({ tipo, id, nombre }),
    }),

  /**
   * Barrido retroactivo: le resuelve el lote a las piezas que tienen coordenada
   * y todavía no pasaron por acá. `forzar` re-mide también las ya barridas —se
   * usa cuando se agregan o corrigen polígonos en `geo.Geometria`.
   */
  barridoLotes: (forzar = false) =>
    pedir<BarridoLotes>('/formaleta/lotes/barrido', {
      method: 'POST', body: JSON.stringify({ forzar }),
    }),

  obras: (q = '') =>
    pedir<ObraFormaleta[]>(`/formaleta/obras${q ? `?q=${encodeURIComponent(q)}` : ''}`),
};

// ── Ayudas de presentación ──────────────────────────────────────────────────

/**
 * Texto contrastante sobre un hex de la base. Los 10 colores de categoría
 * nacieron para una canvas app y NO están validados para contraste accesible
 * ni para sol directo en obra (`#00F5D4` sobre blanco no se lee). Mientras no
 * se revisen con Daniel —cambiarlos es un UPDATE en AdelanteDB, o sea vale con
 * código— la UI los usa como RELLENO y calcula el texto por luminancia, que es
 * lo único que garantiza que la pastilla se lea con cualquiera de los diez.
 */
export function textoSobre(hex: string | null | undefined): string {
  const h = (hex || '').replace('#', '');
  if (h.length !== 6) return '#5D636C';
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  // Luminancia relativa aproximada (ITU-R BT.601), suficiente para elegir
  // entre negro y blanco.
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? '#1A1A1A' : '#FFFFFF';
}

/** `40` y no `40.00`; `50.4` y no `50.40`. */
export function medida(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return String(Number(n));
}

/**
 * La pastilla de medidas de la tarjeta. Devuelve null cuando no hay NINGUNA
 * medida, para que quien la pinte decida mostrar «sin registrar» en rojo en vez
 * de una pastilla vacía (regla de interfaz del expediente).
 */
export function medidasDe(m: { ancho: number | null; largo: number | null; fondo: number | null }): string | null {
  const partes = [m.ancho, m.largo, m.fondo].filter(v => v !== null && v !== undefined) as number[];
  if (!partes.length) return null;
  return partes.map(v => medida(v)).join(' × ') + ' cm';
}

/**
 * ¿Lo que escribió parece un código de pieza y no un nombre de modelo? Decide
 * si el buscador salta al modo «pieza» con el escáner. Acepta `AD-00412`,
 * `ad412` y `412`.
 *
 * El 412 pelado también cuenta a propósito: en campo nadie teclea los ceros ni
 * el prefijo. No puede chocar con un nombre de modelo: medido contra AdelanteDB
 * el 22-oct-2026, de los 404 nombres distintos **ninguno arranca con dígito** y
 * ninguno es solo dígitos.
 */
export function pareceCodigo(q: string): boolean {
  return /^(ad-?)?\d{1,6}$/i.test(q.trim());
}
